import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  UploadSimple, Trash, File, DownloadSimple, FolderOpen, Folder, CaretDown, CaretRight,
  MagnifyingGlass, GridFour, ListBullets,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["W-2", "1099", "K-1", "ID", "Receipts", "Prior return", "Other"];

const CATEGORY_TONE = {
  "W-2": "bg-blue-50 text-blue-700",
  "1099": "bg-emerald-50 text-emerald-700",
  "K-1": "bg-amber-50 text-amber-700",
  "ID": "bg-rose-50 text-rose-700",
  "Receipts": "bg-fuchsia-50 text-fuchsia-700",
  "Prior return": "bg-sky-50 text-sky-700",
  "Other": "bg-slate-100 text-slate-700",
};

function DocRow({ d, onDownload, onDelete, dense = false }) {
  return (
    <tr className="border-t tf-hairline hover:bg-slate-50/60">
      <td className={dense ? "p-2" : "p-3"}>
        <div className="flex items-center gap-2 min-w-0">
          <File size={14} className="text-slate-400 shrink-0" />
          <span className="truncate">{d.filename}</span>
        </div>
      </td>
      <td className={dense ? "p-2" : "p-3"}>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${CATEGORY_TONE[d.category] || "bg-slate-100 text-slate-700"}`}>{d.category}</span>
      </td>
      <td className={dense ? "p-2 text-right font-mono text-xs" : "p-3 text-right font-mono"}>{(d.size / 1024).toFixed(1)} KB</td>
      <td className={dense ? "p-2 font-mono text-[11px] text-slate-500 whitespace-nowrap" : "p-3 font-mono text-xs whitespace-nowrap"}>{(d.uploaded_at || "").split("T")[0]}</td>
      <td className={`text-right whitespace-nowrap ${dense ? "p-2" : "p-3"}`}>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDownload(d)} data-testid={`download-doc-${d.id}`}><DownloadSimple size={14} className="text-[#0A2540]" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(d)} data-testid={`delete-doc-${d.id}`}><Trash size={14} className="text-[#D32F2F]" /></Button>
      </td>
    </tr>
  );
}

function ClientFolder({ ownerName, docs, expanded, onToggle, onDownload, onDelete }) {
  const totalKb = docs.reduce((s, d) => s + (d.size || 0), 0) / 1024;
  return (
    <div className="rounded-md border tf-hairline bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
        data-testid={`folder-${ownerName.replace(/\s+/g, "-").toLowerCase()}`}
      >
        {expanded
          ? <CaretDown size={14} className="text-slate-500 shrink-0" />
          : <CaretRight size={14} className="text-slate-500 shrink-0" />}
        {expanded
          ? <FolderOpen size={20} weight="fill" className="text-[#0A2540] shrink-0" />
          : <Folder size={20} weight="fill" className="text-[#0A2540] shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-[Chivo] font-bold text-[#0A2540] truncate">{ownerName}</div>
          <div className="text-xs text-slate-500 font-mono">
            {docs.length} file{docs.length === 1 ? "" : "s"} · {totalKb.toFixed(1)} KB
          </div>
        </div>
        <div className="flex flex-wrap gap-1 max-w-[40%] justify-end">
          {Array.from(new Set(docs.map(d => d.category))).slice(0, 4).map(cat => (
            <span key={cat} className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${CATEGORY_TONE[cat] || "bg-slate-100 text-slate-700"}`}>{cat}</span>
          ))}
        </div>
      </button>
      {expanded && (
        <table className="w-full text-sm border-t tf-hairline">
          <thead className="bg-slate-50/70 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-2">File</th>
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Size</th>
              <th className="text-left p-2">Uploaded</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <DocRow key={d.id} d={d} dense onDownload={onDownload} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isStaff = user?.role && user.role !== "client";
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("W-2");
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [view, setView] = useState(isStaff ? "folders" : "list"); // folders | list
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const inputRef = useRef(null);
  const [searchParams] = useSearchParams();

  // Deep-link support: /dashboard/documents?client=Alex%20Morgan pre-fills
  // the search box and auto-expands that client's folder for staff.
  useEffect(() => {
    const clientParam = searchParams.get("client");
    if (clientParam) {
      setQ(clientParam);
      if (isStaff) setExpanded((e) => ({ ...e, [clientParam]: true }));
    }
  }, [searchParams, isStaff]);

  useEffect(() => { setView(isStaff ? "folders" : "list"); }, [isStaff]);

  const load = useCallback(async () => {
    const { data } = await api.get("/documents");
    setDocs(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", category);
        await axios.post(`${API_BASE}/documents`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/documents/${toDelete.id}`);
      toast.success("Document removed");
      setToDelete(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API_BASE}/documents/${doc.id}/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter(d =>
      (d.filename || "").toLowerCase().includes(needle) ||
      (d.owner_name || "").toLowerCase().includes(needle) ||
      (d.category || "").toLowerCase().includes(needle),
    );
  }, [docs, q]);

  const grouped = useMemo(() => {
    const map = {};
    for (const d of filtered) {
      const key = d.owner_name || "Unassigned";
      (map[key] ||= []).push(d);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const toggleFolder = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));
  const expandAll = () => setExpanded(Object.fromEntries(grouped.map(([n]) => [n, true])));
  const collapseAll = () => setExpanded({});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="documents-title">Document Upload Center</h1>
          <p className="text-slate-600 mt-1">
            {isStaff
              ? "Every client's uploads, organised into folders. All transfers encrypted at rest."
              : "Everything you upload lives in your personal folder. Encrypted in transit and at rest."}
          </p>
        </div>
        {isStaff && (
          <div className="flex gap-1 rounded-md border tf-hairline bg-white p-1" data-testid="view-toggle">
            <Button
              size="sm"
              variant={view === "folders" ? "default" : "ghost"}
              className={view === "folders" ? "bg-[#0A2540] hover:bg-[#0A2540]/90" : ""}
              onClick={() => setView("folders")}
              data-testid="view-folders"
            >
              <GridFour size={14} className="mr-1" /> Folders
            </Button>
            <Button
              size="sm"
              variant={view === "list" ? "default" : "ghost"}
              className={view === "list" ? "bg-[#0A2540] hover:bg-[#0A2540]/90" : ""}
              onClick={() => setView("list")}
              data-testid="view-list"
            >
              <ListBullets size={14} className="mr-1" /> List
            </Button>
          </div>
        )}
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="rounded-lg border-2 border-dashed border-[#0A2540]/30 bg-white p-8 text-center"
        data-testid="drop-zone"
      >
        <UploadSimple size={34} weight="duotone" className="text-[#0A2540] mx-auto" />
        <div className="mt-2 font-[Chivo] font-bold text-[#0A2540]">Drag & drop files here</div>
        <div className="text-sm text-slate-500">or</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40" data-testid="category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button disabled={uploading} onClick={() => inputRef.current?.click()} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="pick-files-btn">
            {uploading ? "Uploading…" : "Choose files"}
          </Button>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="file-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isStaff ? "Search by file, category or client…" : "Search your files…"} className="pl-9" data-testid="doc-search" />
        </div>
        {isStaff && view === "folders" && (
          <>
            <Button size="sm" variant="outline" onClick={expandAll} data-testid="expand-all">Expand all</Button>
            <Button size="sm" variant="outline" onClick={collapseAll} data-testid="collapse-all">Collapse all</Button>
          </>
        )}
      </div>

      {/* FOLDER VIEW (staff default) */}
      {isStaff && view === "folders" && (
        <div className="space-y-3" data-testid="folders-view">
          {grouped.length === 0 && (
            <div className="rounded-md border tf-hairline bg-white p-10 text-center text-slate-500">
              No documents match this search.
            </div>
          )}
          {grouped.map(([name, list]) => (
            <ClientFolder
              key={name}
              ownerName={name}
              docs={list}
              expanded={!!expanded[name]}
              onToggle={() => toggleFolder(name)}
              onDownload={download}
              onDelete={setToDelete}
            />
          ))}
        </div>
      )}

      {/* LIST VIEW (default for clients; toggle for staff) */}
      {(view === "list" || !isStaff) && (
        <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="documents-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-3">File</th>
                <th className="text-left p-3">Category</th>
                {isStaff && <th className="text-left p-3">Client</th>}
                <th className="text-right p-3">Size</th>
                <th className="text-left p-3">Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={isStaff ? 6 : 5} className="p-6 text-center text-slate-500">No documents uploaded.</td></tr>
              )}
              {filtered.map(d => (
                <tr key={d.id} className="border-t tf-hairline hover:bg-slate-50">
                  <td className="p-3 flex items-center gap-2"><File size={16} /> <span className="truncate max-w-[240px]">{d.filename}</span></td>
                  <td className="p-3">
                    <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${CATEGORY_TONE[d.category] || "bg-slate-100 text-slate-700"}`}>{d.category}</span>
                  </td>
                  {isStaff && <td className="p-3">{d.owner_name}</td>}
                  <td className="p-3 text-right font-mono">{(d.size / 1024).toFixed(1)} KB</td>
                  <td className="p-3 font-mono text-xs">{(d.uploaded_at || "").split("T")[0]}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => download(d)} data-testid={`download-doc-${d.id}`}><DownloadSimple size={14} className="text-[#0A2540]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setToDelete(d)} data-testid={`delete-doc-${d.id}`}><Trash size={14} className="text-[#D32F2F]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `"${toDelete.filename}"`} will be marked as deleted and removed from listings. The underlying object stays retained for compliance. This action can be reversed by a firm administrator on request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-doc-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-doc-confirm">
              Remove document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
